import { QueryKey, Updater, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { Either } from '@playq/irt';
import { GenericFailure } from '@playq/services-shared';

import { getErrorBrief, getErrorMessage } from '/helpers';

import { IUseQueryOptionsExtended } from './types';
import { unwrapEither } from './unwrapEither';

export const useEitherQuery = <LeftType, RightType>(
  queryKeys: QueryKey,
  queryFn: () => Promise<Either<LeftType, RightType | undefined>> | undefined,
  options?: IUseQueryOptionsExtended<RightType | undefined, LeftType | Error>
) => {
  const queryClient = useQueryClient();

  const res = useQuery({
    queryKey: queryKeys,
    queryFn: () => {
      const queryRes = queryFn();
      if (queryRes !== undefined) {
        return unwrapEither(queryRes);
      }
    },
    initialData: () => queryClient.getQueryData<RightType | undefined>(queryKeys),
    ...options,
  });

  const { error } = res;
  const errorMessage = useMemo(() => {
    if (error instanceof GenericFailure || error instanceof Error) {
      return getErrorMessage(error);
    }
  }, [error]);

  const errorBrief =
    error instanceof GenericFailure || error instanceof Error ? (getErrorBrief(error) ?? errorMessage) : undefined;

  const setQueryData = useCallback(
    (keys: QueryKey, updator: Updater<RightType | undefined, RightType | undefined>) => {
      queryClient.setQueryData<RightType | undefined>(keys, updator);
    },
    [queryClient]
  );

  const mutate = useCallback(
    (updator: Updater<RightType | undefined, RightType | undefined>) => {
      queryClient.setQueryData<RightType | undefined>(queryKeys, updator);
    },
    [queryClient, queryKeys]
  );

  return {
    ...res,
    keys: queryKeys,
    mutate,
    setQueryData,
    errorMessage,
    errorBrief,
  };
};
